<template>
  <!-- display card if  apprenticeships exist-->
  <v-card class="pb-8" flat>
    <v-card flat class="v-card__container">
      <v-data-table
        :headers="headers" 
        :items="filteredWorkplaces" 
        sort-by="name" 
        item-key="id" 
        class="mt-0"
        >
        <!-- <template v-slot:item.action="{item}"> -->
        <template slot="item.action" slot-scope="props">
          <div style="width: 60px" class="d-flex justify-space-between">
            <v-icon @click.stop="editItem(props.item)" :disabled="!canUpdate">$sl-edit</v-icon>
            <v-icon @click.stop="deleteItem(props.item)" :disabled="!canUpdate">$sl-delete</v-icon>
          </div>
        </template>
        <template slot="item.postalZip" slot-scope="props">
          <div style="width: 60px">
            {{ formatZip(props.item.postalZip) }}
          </div>
        </template>
        <template slot="item.email" slot-scope="props">
          <div style="width: 180px">
            <a :href="'mailto:' + props.item.email">{{ props.item.email }}</a>
          </div>
        </template>
        <template
          slot="item.departmentWorkplaces"
          slot-scope="props"
          v-if="selectedCompany && selectedCompany.hasDepartments"
        >
          <div style="width: 180px">
            {{ depCommaSep(props.item.departmentWorkplaces) ? depCommaSep(props.item.departmentWorkplaces) : '' }}
          </div>
        </template>
        <template slot="item.isInvoiceWorkplace" slot-scope="props">
          <div style="width: 90px">
            <v-list-item-icon class="mr-n2" v-if="props.item.isDeliveryWorkplace">
              <v-icon>$sl-shipment-box</v-icon>
            </v-list-item-icon>
            <v-list-item-icon v-if="props.item.isInvoiceWorkplace">
              <v-icon>$sl-accounting-invoice</v-icon>
            </v-list-item-icon>
          </div>
        </template>
      </v-data-table>
    </v-card>
    <v-btn data-cy="add-new-workplace" absolute bottom right fab class="v-btn--add-on-list" @click="newItem" :disabled="!canUpdate">
      <v-icon class="ml-1">$sl-add</v-icon>
    </v-btn>

    <!-- The Dialog -->
    <v-dialog v-model="wpModal" max-width="600px">
      <CompanyWorkplaceModal :reRender="reRender" :wp="wp" @close="close" @save="save"></CompanyWorkplaceModal>
    </v-dialog>
  </v-card>
</template>

<script>
import API from '@/services/api.js'

import {mapGetters, mapActions} from 'vuex'
import {pageSize} from '@/store/constants'
import get from 'lodash/get'
import findIndex from 'lodash/findIndex'
import filter from 'lodash/filter'
import intersection from 'lodash/intersection'
import CompanyWorkplaceModal from '@/components/company/CompanyWorkplaceModal'
import { CanIDo } from '@/components/mixins'

// @group Company
// Styled CompanyWorkplaces component
export default {
  name: 'CompanyWorkplaces',
  mixins:[CanIDo],
  props: {
    reRender: {
      type: Boolean,
      default: false
    },

    filters: {
      type: Object,
      default: {}
    }
  },

  data() {
    return {
      workplaces: [],
      wp: {},
      wpModal: false
    }
  },

  components: {
    CompanyWorkplaceModal
  },

  watch: {
    reRender(newValue, oldValue) {
      this.refreshList()
    },
  },

  created() {
    this.getWorkplaces()
  },

  computed: {
    ...mapGetters('company', ['selectedCompany']),

    filteredWorkplaces() {
      const wcMatch = (wpArr, search) => {
        return filter(wpArr, o => {
          const criteria = new RegExp(search, "i")
          return (o.name && o.name.match(criteria)) || 
            (o.postalAddress1 && o.postalAddress1.match(criteria)) || 
            (o.postalZip && o.postalZip.match(criteria)) || 
            (o.postalCity && o.postalCity.match(criteria)) || 
            (o.phone && o.phone.match(criteria)) || 
            (o.email && o.email.match(criteria));
        })
      }

      if (this.filters.advancedMode) {
        let matches = this.workplaces
        const wildcard = get(this, 'filters.advSearch.workplaceSearch', null)
        if (wildcard) {
          matches = wcMatch(matches, wildcard)
        }
        const address = get(this, 'filters.advSearch.workplaceAddress', null)
        if (address) {
          const criteria = new RegExp(address, "i")
          matches = filter(matches, o => o.postalAddress1.match(criteria) )
        }
        const city = get(this, 'filters.advSearch.workplaceCity', null)
        if (city) {
          const criteria = new RegExp(city, "i")
          matches = filter(matches, o => o.postalCity.match(criteria) )
        }
        const zip = get(this, 'filters.advSearch.workplaceZip', null)
        if (zip) {
          const criteria = new RegExp(zip, "i")
          matches = filter(matches, o => o.postalZip.match(criteria) )
        }
        const dep = get(this, 'filters.advSearch.workplaceDepartments', null)
        if (dep && dep.length) {
          matches = filter(matches, o => {
            const inSect = intersection(dep, o.departmentWorkplaces.map(o => o.department.id))
            return inSect.length
          })
        }
        
        return matches
      } else {
        const wildcard = get(this, 'filters.search', null)
        if (wildcard) {
          return wcMatch(this.workplaces, this.filters.search)
        }
      }
      //- Default is all
      return this.workplaces
    },

    headers() {
      const h = [
        {text: 'Namn', value: 'name', width: '230', sortable: true, show: true},
        {text: 'Adress', value: 'postalAddress1', width: '165', sortable: false, show: true},
        {text: 'Postnr.', value: 'postalZip', width: '120', sortable: false, show: true},
        {text: 'Ort', value: 'postalCity', sortable: false, show: true},
        {text: 'Telefon', value: 'phone', sortable: false, show: true},
        {text: 'E-post', value: 'email', sortable: false, show: true},
        {
          text: 'Avdelningar',
          value: 'departmentWorkplaces',
          sortable: false,
          show: this.selectedCompany && this.selectedCompany.hasDepartments ? true : false
        },
        {text: 'LEV./FAKT.', value: 'isInvoiceWorkplace', sortable: false, width: '90', show: true},
        {text: 'Ändra', value: 'action', sortable: false, width: '90', show: true}
      ]
      return filter(h, 'show')
    }
  },

  asyncComputed: {
    canUpdate(){
      return this.canIDo('company', 'update');
    },
  },

  methods: {
    ...mapActions('company', ['setSelectedCompany', 'updateSelectedCompany']),

    getWorkplaces() {
      this.workplaces = this.selectedCompany.workplaces
    },

    depCommaSep(departments) {
      return departments
        .map(d => {
          return get(d, 'department.name', '')
        })
        .join(', ')
    },

    formatZip(zip) {
      var ret
      if (zip && zip.length == 5) {
        ret = zip.substring(0, 3) + ' ' + zip.substring(3, 5)
      }
      return ret
    },

    newItem() {
      this.editedIndex = -1
      //- Set to "model"
      this.wp = {}
      this.wpModal = true
    },

    /**
     * @vuese
     * Used to update workplace or create a new one if it exist
     * @arg The argument is an integer representing application  id and param workplace object
     */
    save(editedItem) {
      if (editedItem.id > 0) {
        API.updateWorkplace(this.selectedCompany.id, editedItem.id, {data: editedItem})
          .then(result => {
            this.$events.fire('showInfo', 'Sparad.')
            this.refreshList()
          })
          .catch(error => {
            this.$events.fire('showError', 'Tyvärr gick det inte att spara ändringarna.')
            console.log('There was an error:', error)
          })
      } else {
        API.createWorkplace(this.selectedCompany.id, {data: editedItem})
          .then(result => {
            this.$events.fire('showInfo', 'Sparad.')
            this.refreshList()
          })
          .catch(error => {
            this.$events.fire('showError', 'Tyvärr gick det inte att spara.')
            console.log('There was an error:', error)
          })
      }
      this.wpModal = false
    },

    editItem(item) {
      if (this.selectedCompany.id && item.id) {
        API.getWorkplace(this.selectedCompany.id, item.id)
          .then(response => {
            this.wp = response.data
            this.wpModal = true
          })
          .catch(error => {
            console.log('error', error)
          })
      } else {
        this.$events.fire('showError', 'Tyvärr gick det inte att ändra.')
      }
    },

    async deleteItem(item) {
      const res = await this.$dialog.confirm({
        title: 'Vill du verkligen radera denna arbetsplats?',
        text: 'Radering av arbetsplats går inte att ångra.',
        actions: [
          {text: 'Avbryt', key: false, icon: '$sl-cancel'},
          {text: 'Radera', key: true, icon: '$sl-delete', outlined: true}
        ]
      })

      if (res === true) {
        const index = findIndex(this.workplaces, e => {
          return e.id == item.id
        })

        API.deleteWorkplace(this.selectedCompany.id, item.id)
          .then(reponse => {
            this.workplaces.splice(index, 1)
            this.$events.fire('showInfo', 'Raderad.')
          })
          .catch(error => {
            this.$events.fire(
              'showError',
              'Ett fel uppstod (' + error.message + '), om detta händer igen v.v. kontakta BYN:s kansli.'
            )
            console.log('There was an error:', error)
          })
      }
    },

    close() {
      this.wpModal = false
    },

    refreshList() {
      this.workplaces = []
      API.getCompany(this.selectedCompany.id)
        .then(res => {
          // @vuese
          // * Save companies to store
          this.setSelectedCompany(res.data)
          this.workplaces = this.selectedCompany.workplaces
        })
        .catch(error => {
          console.log('There was an error:', error.response)
        })
    }
  }
}
</script>

<style scoped></style>
